/**
 * Delay step settings as the editor reads and writes them (BB-116): the shape of `DelayConfig` in `@bb/domain`
 * (`steps/delay-compute.ts`), which the dashboard mirrors because it does not depend on the domain package. The server
 * checks the same rules when it validates the draft.
 */

/** The longest delay (Braze: 730 days). */
export const MAX_DELAY_SECONDS = 730 * 86_400;

export type DelayKind = 'duration' | 'until_date' | 'day_of_week' | 'personalized';
export type OnPast = 'exit' | 'advance_now';
export type OffsetUnit = 'minutes' | 'hours' | 'days' | 'weeks';
export type DelayOffset = { amount: number; unit: OffsetUnit; direction: 'before' | 'after' };

export type DelayConfig =
  | { kind: 'duration'; seconds: number }
  | { kind: 'until_date'; date: string; time: string; localTime: boolean; onPast: OnPast }
  | { kind: 'day_of_week'; weekdays: number[]; time: string; localTime: boolean; lateOnChosenDay: 'next_occurrence' | 'advance_now' }
  | { kind: 'personalized'; source: { from: 'context' | 'attribute'; key: string }; time?: string; offset?: DelayOffset; localTime: boolean; onInvalid: 'exit' | 'advance'; onPast: OnPast };

export const DELAY_KINDS: Array<{ value: DelayKind; label: string }> = [
  { value: 'duration', label: 'For a set time' },
  { value: 'until_date', label: 'Until a calendar date' },
  { value: 'day_of_week', label: 'Until a day of the week' },
  { value: 'personalized', label: 'Until a time from the user’s data' },
];

export const DEFAULT_DELAY_TIME = '09:00';
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const KEY_MAX = 255;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback);
const bool = (v: unknown, fallback: boolean) => (typeof v === 'boolean' ? v : fallback);
const oneOf = <T extends string>(v: unknown, values: readonly T[], fallback: T): T => (values.includes(v as T) ? (v as T) : fallback);

export const isTime = (v: string) => TIME.test(v);
/** A real calendar date `YYYY-MM-DD` (not 2026-02-30). */
export const isIsoDate = (v: string) => DATE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v;

/**
 * The stored settings with the server's defaults filled in and unknown keys left out. A config without `kind` is a
 * duration (design-time graphs and templates used `{ seconds }` before the handler shipped).
 */
export function readDelay(raw: unknown): DelayConfig {
  const c = isObject(raw) ? raw : {};
  const localTime = bool(c.localTime, true);
  switch (c.kind) {
    case 'until_date':
      return { kind: 'until_date', date: str(c.date, ''), time: str(c.time, '00:00'), localTime, onPast: oneOf(c.onPast, ['exit', 'advance_now'], 'exit') };
    case 'day_of_week':
      return {
        kind: 'day_of_week',
        weekdays: Array.isArray(c.weekdays) ? c.weekdays.filter((d): d is number => typeof d === 'number') : [],
        time: str(c.time, ''),
        localTime,
        lateOnChosenDay: oneOf(c.lateOnChosenDay, ['next_occurrence', 'advance_now'], 'next_occurrence'),
      };
    case 'personalized': {
      const source = isObject(c.source) ? c.source : {};
      const offset = isObject(c.offset) ? c.offset : undefined;
      return {
        kind: 'personalized',
        source: { from: oneOf(source.from, ['context', 'attribute'], 'attribute'), key: str(source.key, '') },
        ...(typeof c.time === 'string' ? { time: c.time } : {}),
        ...(offset ? { offset: { amount: typeof offset.amount === 'number' ? offset.amount : 0, unit: oneOf(offset.unit, ['minutes', 'hours', 'days', 'weeks'], 'days'), direction: oneOf(offset.direction, ['before', 'after'], 'before') } } : {}),
        localTime,
        onInvalid: oneOf(c.onInvalid, ['exit', 'advance'], 'exit'),
        onPast: oneOf(c.onPast, ['exit', 'advance_now'], 'exit'),
      };
    }
    default:
      return { kind: 'duration', seconds: typeof c.seconds === 'number' ? c.seconds : 86_400 };
  }
}

/** Settings of another kind: the time of day and the time zone carry over where both kinds have them. */
export function switchDelayKind(current: DelayConfig, kind: DelayKind): DelayConfig {
  if (current.kind === kind) return current;
  const time = 'time' in current && current.time && isTime(current.time) ? current.time : DEFAULT_DELAY_TIME;
  const localTime = 'localTime' in current ? current.localTime : true;
  switch (kind) {
    case 'duration':
      return { kind, seconds: 86_400 };
    case 'until_date':
      return { kind, date: '', time, localTime, onPast: 'exit' };
    case 'day_of_week':
      return { kind, weekdays: [1], time, localTime, lateOnChosenDay: 'next_occurrence' };
    case 'personalized':
      return { kind, source: { from: 'attribute', key: '' }, localTime, onInvalid: 'exit', onPast: 'exit' };
  }
}

/** The schema's rules as messages per field (`seconds`, `date`, `time`, `weekdays`, `key`, `offset`). */
export function delayIssues(c: DelayConfig): Record<string, string> {
  const issues: Record<string, string> = {};
  switch (c.kind) {
    case 'duration':
      if (!Number.isInteger(c.seconds) || c.seconds < 1) issues.seconds = 'Wait at least 1 second.';
      else if (c.seconds > MAX_DELAY_SECONDS) issues.seconds = 'Wait at most 730 days.';
      break;
    case 'until_date':
      if (!isIsoDate(c.date)) issues.date = 'Choose a date.';
      if (!isTime(c.time)) issues.time = 'Enter a time (HH:MM).';
      break;
    case 'day_of_week':
      if (c.weekdays.length === 0) issues.weekdays = 'Choose at least one day.';
      if (!isTime(c.time)) issues.time = 'Enter a time (HH:MM).';
      break;
    case 'personalized':
      if (!c.source.key.trim()) issues.key = c.source.from === 'context' ? 'Enter the context variable.' : 'Enter the attribute.';
      else if (c.source.key.length > KEY_MAX) issues.key = `At most ${KEY_MAX} characters.`;
      if (c.time !== undefined && !isTime(c.time)) issues.time = 'Enter a time (HH:MM).';
      if (c.offset && (!Number.isInteger(c.offset.amount) || c.offset.amount < 0 || c.offset.amount > MAX_DELAY_SECONDS)) issues.offset = 'Enter a whole number, 0 or more.';
      break;
  }
  return issues;
}
